import { useState } from "react";
import axios from "axios";
import { Download, Check } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEXTS = {
  de: {
    kicker: "Gratis-Download",
    title: "Der komplette Smart-Mirror-Guide als PDF",
    sub: "Alle Kapitel dieser Seite kompakt zum Mitnehmen: Technik, Kosten, Anbieter-Überblick und die 5-Schritte-Checkliste für Ihre Boutique.",
    placeholder: "Ihre E-Mail-Adresse",
    btn: "Guide als PDF herunterladen",
    sending: "Wird gesendet …",
    success: "Der Download startet – wir haben Ihnen den Link zusätzlich per E-Mail geschickt.",
    error: "Das hat leider nicht geklappt. Bitte versuchen Sie es erneut.",
    privacy: "Kein Spam. Wir melden uns nur mit relevanten Infos für Boutiquen.",
  },
  en: {
    kicker: "Free download",
    title: "The complete smart mirror guide as a PDF",
    sub: "Every chapter of this page in one compact file: technology, costs, provider overview and the 5-step checklist for your boutique.",
    placeholder: "Your email address",
    btn: "Download the guide as PDF",
    sending: "Sending …",
    success: "Your download is starting – we've also emailed you the link.",
    error: "Something went wrong. Please try again.",
    privacy: "No spam. We only reach out with relevant info for boutiques.",
  },
};

export default function GuideDownload() {
  const { lang } = useLang();
  const t = lang === "de" ? TEXTS.de : TEXTS.en;
  const glang = lang === "de" ? "de" : "en";
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      await axios.post(`${API}/guide`, { email, lang: glang });
      setStatus("success");
      window.open(`${API}/guide/file/${glang}`, "_blank");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="mt-10 rounded-3xl border-2 p-7 sm:p-9" style={{ borderColor: "var(--gold)", background: "var(--surface-secondary)" }} data-testid="guide-download-section">
      <p className="text-xs font-bold uppercase" style={{ letterSpacing: "0.14em", color: "var(--camel)" }}>✦ {t.kicker}</p>
      <h2 className="mt-3 font-serif-display text-2xl sm:text-3xl font-semibold leading-snug">{t.title}</h2>
      <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--muted-warm)" }}>{t.sub}</p>

      {status === "success" ? (
        <div className="mt-6 rounded-2xl border p-4" style={{ borderColor: "var(--gold)", background: "var(--surface)" }} data-testid="guide-success">
          <p className="flex items-start gap-2.5 text-sm font-medium">
            <Check size={17} className="mt-0.5 shrink-0" style={{ color: "var(--camel)" }} /> {t.success}
          </p>
          <a
            href={`${API}/guide/file/${glang}`}
            target="_blank"
            rel="noreferrer"
            data-testid="guide-direct-link"
            className="mt-3 inline-flex items-center gap-2 text-sm font-semibold gold-underline"
            style={{ color: "var(--camel)" }}
          >
            <Download size={14} /> {t.btn}
          </a>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 flex flex-col gap-3 sm:flex-row">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.placeholder}
            data-testid="guide-email-input"
            className="flex-1 rounded-full border px-5 py-3.5 text-sm outline-none transition-colors focus:border-[var(--camel)]"
            style={{ borderColor: "var(--line)", background: "var(--surface)", color: "var(--on-surface)" }}
          />
          <button
            type="submit"
            disabled={status === "sending"}
            data-testid="guide-submit-btn"
            className="pill-btn flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: "var(--camel)" }}
          >
            <Download size={15} /> {status === "sending" ? t.sending : t.btn}
          </button>
        </form>
      )}
      {status === "error" && <p className="mt-3 text-sm" style={{ color: "#B3433B" }} data-testid="guide-error">{t.error}</p>}
      <p className="mt-4 text-xs" style={{ color: "var(--muted-warm)" }}>{t.privacy}</p>
    </div>
  );
}
